import { makeStyles } from '@mui/styles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip, Alert, Button, CircularProgress } from '@mui/material';
import DepositLayout from '../../components/updated/Deposit';
import { CopyFilled } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import bsDollar from '../../assets/arts/usd icon 1.svg';
import InfoIcon from '@mui/icons-material/Info';
import {
  decodeJWT,
  getUserDetails,
  createFiatDepositNew,
} from '../../services/api';

const useStyles = makeStyles((theme) => ({
  heading: {
    fontSize: '30px',
    fontWeight: '600',
    color: `${theme.palette.text.primary} !important`,
    textAlign: 'center',
  },
  secondaryHeading: {
    fontSize: '14px',
    color: `${theme.palette.text.secondary} !important`,
    textAlign: 'center',
    marginBottom: '30px',
  },
  container: { padding: '20px' },
  card: {
    padding: '20px',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    marginBottom: '20px',
  },
  field: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
    gap: '12px',
  },
  label: { fontWeight: 'bold' },
  value: { display: 'flex', alignItems: 'center' },
  rowNote: {
    marginTop: 8,
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  footerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 10,
    flexWrap: 'wrap',
  },
}));

const DepositAddAccountInfo = () => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    currency = 'USD',
    amount = 0,
    paymentMethod = 'Wire Transfer',
  } = location.state || {};

  const [email, setEmail] = useState(localStorage.getItem('email') || '');
  const [userData, setUserData] = useState(null);
  const [copiedField, setCopiedField] = useState('');
  const [creating, setCreating] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [orderResp, setOrderResp] = useState(null);
  const [approveUrl, setApproveUrl] = useState('');
  const calledOnceRef = useRef(false);

  // Normalize incoming "paymentMethod" to a stable set
  const normalizedPaymentType = useMemo(() => {
    const pm = String(paymentMethod).toLowerCase();
    if (pm.includes('paypal')) return 'Paypal';
    if (pm.includes('credit')) return 'CreditCard';
    if (pm.includes('wire')) return 'Wire Transfer';
    if (pm.includes('ach')) return 'ACH';
    if (pm.includes('zelle')) return 'Zelle';
    if (pm.includes('venmo')) return 'Venmo';
    if (pm.includes('tyga')) return 'TygaPay';
    return paymentMethod || '';
  }, [paymentMethod]);

  useEffect(() => {
    const accessToken = localStorage.getItem('access_token');
    let emailFromToken = '';

    if (accessToken && accessToken !== 'null' && accessToken !== 'undefined') {
      const decoded = decodeJWT(String(accessToken));
      if (decoded && decoded.email) {
        emailFromToken = decoded.email;
        setEmail(emailFromToken);
      }
    }

    const emailToUse = emailFromToken || email;
    if (emailToUse) {
      getUserDetails(emailToUse).then((res) => {
        if (res.status === 200) setUserData(res.data);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create fiat deposit order once, then route based on payment method
  useEffect(() => {
    const canCall =
      !calledOnceRef.current &&
      email &&
      currency &&
      Number(amount) > 0 &&
      normalizedPaymentType;

    if (!canCall) return;

    const run = async () => {
      try {
        setCreating(true);
        setApiError(null);
        calledOnceRef.current = true;

        const payload = {
          email,
          coin: currency,
          amount: Number(amount),
          paymentType: normalizedPaymentType,
        };

        const resp = await createFiatDepositNew(payload);
        setOrderResp(resp);

        // Extract common fields safely
        const data = resp?.data ?? resp; // depending on your API wrapper
        const orderId =
          data?.orderId || data?.paypalId || data?.data?.orderId || '';
        const links = data?.links || data?.data?.links || [];
        const approve =
          links?.find?.((l) => String(l?.rel).toLowerCase() === 'approve') ||
          null;
        const payPalPaymentLink =
          data?.payPalPaymentLink || data?.approvalUrl || approve?.href || '';

        // Route by payment type
        const pt = normalizedPaymentType;
        if (pt === 'Paypal' || pt === 'CreditCard') {
          if (!payPalPaymentLink) {
            setApproveUrl('');
            setApiError('Unable to get PayPal payment link. Please retry.');
            return;
          }
          setApproveUrl(payPalPaymentLink);
          // Navigate to bridge page and pass link in state
          navigate('/paypal-partnership-with-indexx', {
            state: { payPalPaymentLink },
            replace: false,
          });
          return;
        }

        if (pt === 'Zelle') {
          if (!orderId) {
            setApiError('Missing orderId for Zelle flow.');
            return;
          }
          navigate(
            `/indexx-exchange/payment-zelle?orderId=${encodeURIComponent(
              orderId
            )}`
          );
          return;
        }

        if (pt === 'Venmo') {
          if (!orderId) {
            setApiError('Missing orderId for Venmo flow.');
            return;
          }
          navigate(
            `/indexx-exchange/payment-venmo?orderId=${encodeURIComponent(
              orderId
            )}`
          );
          return;
        }

        // For bank-like (Wire/ACH), we stay put and show instructions
      } catch (e) {
        setApiError(
          e?.response?.data?.message || e?.message || 'Failed to create deposit'
        );
      } finally {
        setCreating(false);
      }
    };

    run();
  }, [email, currency, amount, normalizedPaymentType, navigate]);

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text || '');
    setCopiedField(field);
    setTimeout(() => setCopiedField(''), 2000);
  };

  const isBankLike =
    normalizedPaymentType === 'Wire Transfer' ||
    normalizedPaymentType === 'ACH';

  const infoBannerText = isBankLike
    ? 'Please include your reference code in the comments. Transfers with missing/incorrect code will be rejected.'
    : `You selected ${paymentMethod}. You will be directed to complete your payment using the selected provider.`;

  // Helpful bits from the order (if present)
  const orderId = orderResp?.data?.orderId || orderResp?.data?.paypalId || '';
  const orderStatus = orderResp?.data?.status || '';

  return (
    <DepositLayout>
      <div className={classes.container}>
        <h3 className={classes.heading}>
          {isBankLike
            ? 'Transfer money to process order'
            : 'Complete your payment'}
        </h3>
        <h4 className={classes.secondaryHeading}>
          {isBankLike
            ? 'Please transfer your funds to the account below'
            : `Selected method: ${paymentMethod}`}
        </h4>

        <Alert
          icon={<InfoIcon fontSize="large" />}
          severity={apiError ? 'error' : 'info'}
          style={{
            marginTop: '20px',
            backgroundColor: apiError
              ? 'rgba(255, 0, 0, 0.08)'
              : 'rgba(95, 95, 95, 0.1)',
            borderColor: 'transparent',
          }}
        >
          {apiError ? apiError : infoBannerText}
        </Alert>

        {/* Summary */}
        <div className={classes.card}>
          <div className={classes.field}>
            <span className={classes.label}>Currency:</span>
            <div className={classes.value}>
              <img
                src={bsDollar}
                alt="currency"
                width="25"
                style={{ marginRight: '10px' }}
              />
              <span>{currency}</span>
            </div>
          </div>
          <div className={classes.field}>
            <span className={classes.label}>Amount:</span>
            <span>{Number(amount).toLocaleString()}</span>
          </div>
          <div className={classes.field}>
            <span className={classes.label}>Payment Method:</span>
            <span>{paymentMethod}</span>
          </div>

          {/* Order meta (once created) */}
          {(creating || orderId || orderStatus) && (
            <>
              <div className={classes.field}>
                <span className={classes.label}>Order Status:</span>
                <span>
                  {creating ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <CircularProgress size={16} /> Creating…
                    </span>
                  ) : (
                    orderStatus || '-'
                  )}
                </span>
              </div>
              <div className={classes.field}>
                <span className={classes.label}>Order ID:</span>
                <div className={classes.value}>
                  <span>{orderId || '-'}</span>
                  {orderId && (
                    <Tooltip
                      title={
                        copiedField === 'orderId' ? 'Copied' : 'Click To Copy'
                      }
                    >
                      <CopyFilled
                        style={{ marginLeft: '10px', cursor: 'pointer' }}
                        onClick={() => handleCopy(orderId, 'orderId')}
                      />
                    </Tooltip>
                  )}
                </div>
              </div>

              {/* For PayPal/CreditCard, we navigate away; if we somehow stayed, show a fallback button */}
              {!isBankLike && approveUrl && (
                <div className={classes.footerRow}>
                  <Button
                    variant="contained"
                    onClick={() =>
                      window.open(approveUrl, '_blank', 'noopener,noreferrer')
                    }
                  >
                    Continue in {paymentMethod}
                  </Button>
                  <span className={classes.rowNote}>
                    If it didn’t open, click “Continue” to launch{' '}
                    {paymentMethod} again.
                  </span>
                </div>
              )}
            </>
          )}

          <div className={classes.field}>
            <span className={classes.label}>Reference Code:</span>
            <div className={classes.value}>
              <span>{userData?._id || '-'}</span>
              <Tooltip
                title={copiedField === 'reference' ? 'Copied' : 'Click To Copy'}
              >
                <CopyFilled
                  style={{ marginLeft: '10px', cursor: 'pointer' }}
                  onClick={() => handleCopy(userData?._id, 'reference')}
                />
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Bank-like details or provider note */}
        {isBankLike ? (
          <div className={classes.card}>
            <div className={classes.field}>
              <span className={classes.label}>Beneficiary Account Name:</span>
              <div className={classes.value}>
                <span>Indexx</span>
                <Tooltip
                  title={
                    copiedField === 'accountName' ? 'Copied' : 'Click To Copy'
                  }
                >
                  <CopyFilled
                    style={{ marginLeft: '10px', cursor: 'pointer' }}
                    onClick={() => handleCopy('Indexx', 'accountName')}
                  />
                </Tooltip>
              </div>
            </div>
            <div className={classes.field}>
              <span className={classes.label}>
                Beneficiary Account Number/IBAN:
              </span>
              <div className={classes.value}>
                <span>1793811546</span>
                <Tooltip
                  title={
                    copiedField === 'accountNumber' ? 'Copied' : 'Click To Copy'
                  }
                >
                  <CopyFilled
                    style={{ marginLeft: '10px', cursor: 'pointer' }}
                    onClick={() => handleCopy('1793811546', 'accountNumber')}
                  />
                </Tooltip>
              </div>
            </div>
            <div className={classes.field}>
              <span className={classes.label}>Beneficiary Address:</span>
              <div className={classes.value}>
                <span style={{ textAlign: 'right' }}>
                  Office 22 Alpha, Los Angeles, California
                </span>
                <Tooltip
                  title={copiedField === 'address' ? 'Copied' : 'Click To Copy'}
                >
                  <CopyFilled
                    style={{ marginLeft: '10px', cursor: 'pointer' }}
                    onClick={() =>
                      handleCopy(
                        'Office 22 Alpha, Los Angeles, California',
                        'address'
                      )
                    }
                  />
                </Tooltip>
              </div>
            </div>
            {normalizedPaymentType === 'ACH' && orderResp?.data?.status && (
              <div className={classes.rowNote}>
                ACH status: <strong>{orderResp.data.status}</strong> (use the
                Reference Code in your transfer memo).
              </div>
            )}
          </div>
        ) : (
          <div className={classes.card}>
            <div className={classes.field} style={{ justifyContent: 'center' }}>
              <span>
                You’ll be guided to {paymentMethod} to finish the payment. If
                the page doesn’t open automatically, use the button above to
                continue.
              </span>
            </div>
          </div>
        )}
      </div>
    </DepositLayout>
  );
};

export default DepositAddAccountInfo;
